import { useLayoutEffect, useCallback, type RefObject } from 'react';

const NUM_VALUE_COLS = 6;

function findPasteCellTd(e: KeyboardEvent, host: HTMLElement): HTMLTableCellElement | null {
    const fromNode = (node: EventTarget | null): HTMLTableCellElement | null => {
        if (!(node instanceof Element)) return null;
        const td = node.closest('td[data-line-paste-cell]');
        return td instanceof HTMLTableCellElement && host.contains(td) ? td : null;
    };

    // Prefer composedPath (target + ancestors) so we still resolve the cell when `e.target` is odd
    // (e.g. text nodes, retargeting, or focus vs target mismatch on some browsers).
    let path: EventTarget[] = [];
    try {
        path = typeof e.composedPath === 'function' ? e.composedPath() : [];
    } catch {
        path = [];
    }
    for (let i = 0; i < path.length; i++) {
        const hit = fromNode(path[i]);
        if (hit) return hit;
    }

    const fromActive = fromNode(document.activeElement);
    if (fromActive) return fromActive;

    return fromNode(e.target);
}

/** Numpad with NumLock off still reports `key` as Arrow*; NumLock on sends digits — we do not intercept those. */
const GRID_ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** Numpad Enter often uses `code` only; some environments use `key` `NumpadEnter`. */
function isGridEnterKey(e: KeyboardEvent): boolean {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') return true;
    return e.code === 'NumpadEnter';
}

/** Arrow keys (main + numpad with NumLock off both report `key` as Arrow* in modern browsers). */
function gridArrowDirection(e: KeyboardEvent): 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | null {
    const { key } = e;
    if (GRID_ARROW_KEYS.has(key)) return key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
    return null;
}

/** Use the focused field inside the cell (not always `e.target` after Chakra/event retargeting). */
function caretBlocksHorizontalCellNav(td: HTMLTableCellElement, e: KeyboardEvent, dir: 'left' | 'right'): boolean {
    const ae = document.activeElement;
    let el: HTMLInputElement | HTMLTextAreaElement | null = null;
    if (ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement) {
        if (td.contains(ae)) el = ae;
    }
    if (!el && (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        if (td.contains(e.target)) el = e.target;
    }
    if (!el || el.disabled || el.readOnly) return false;
    const rawStart = el.selectionStart;
    const rawEnd = el.selectionEnd;
    if (rawStart == null || rawEnd == null) return false;
    const start = rawStart;
    const end = rawEnd;
    if (start !== end) return true;
    const len = el.value.length;
    if (dir === 'left') return start > 0;
    return end < len;
}

/** Chakra Editable keeps a hidden input in the DOM; `querySelector('input')` can grab it before edit mode commits. */
export function findVisibleEditableInputInPasteTd(td: Element): HTMLInputElement | null {
    const nodes = td.querySelectorAll('input.chakra-editable__input');
    for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i];
        if (!(el instanceof HTMLInputElement) || el.disabled) continue;
        if (el.hasAttribute('hidden')) continue;
        return el;
    }
    return null;
}

function markEditableInputCommitOnBlur(td: HTMLTableCellElement): void {
    const inp = findVisibleEditableInputInPasteTd(td);
    if (!inp) return;
    inp.dataset.commitOnBlur = '1';
}

/** Highest data-row on description cells in the grid (avoids stale lineItemCount skipping Tab wrap off last column). */
function lastPasteRowIndexFromDom(host: HTMLElement): number {
    let max = -1;
    host.querySelectorAll('td[data-line-paste-cell][data-col="0"]').forEach((el) => {
        const r = parseInt(el.getAttribute('data-row') || '', 10);
        if (Number.isFinite(r)) max = Math.max(max, r);
    });
    return max;
}

/** After value is seeded (type-to-replace), open the cell editor and focus the input. */
export function focusOpenPasteCellEditor(host: HTMLElement, row: number, col: number): void {
    const td = host.querySelector(`td[data-line-paste-cell][data-row="${row}"][data-col="${col}"]`);
    if (!td) return;
    const colNum = parseInt(td.getAttribute('data-col') || '', 10);
    if (!Number.isFinite(colNum)) return;
    if (colNum >= 3) {
        window.setTimeout(() => {
            (td.querySelector('input:not([hidden])') as HTMLInputElement | null)?.focus();
        }, 0);
        return;
    }
    const preview =
        (td.querySelector('[data-line-item-editable-preview]') as HTMLElement | null) ??
        (td.querySelector('.chakra-editable__preview') as HTMLElement | null);
    preview?.focus({ preventScroll: true });
    preview?.click();
    const tryFocusEditable = (attempt: number) => {
        const inp = findVisibleEditableInputInPasteTd(td);
        if (inp) {
            inp.focus();
            return;
        }
        if (attempt < 28) window.requestAnimationFrame(() => tryFocusEditable(attempt + 1));
        else findVisibleEditableInputInPasteTd(td)?.focus();
    };
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => tryFocusEditable(0));
    });
}

/**
 * Tab / Enter / Arrow navigation across line-item value cells (Excel-style: Tab = right / next row,
 * Enter = down, arrows move one cell; Left/Right respect text caret until at start/end).
 * Uses window capture + composedPath/activeElement so cells work inside Chakra Tabs/Table/Editable.
 */
export function useLineItemGridCellKeyboardNav(options: {
    containerRef: RefObject<HTMLDivElement | null>;
    lineItemCount: number;
    setEditingNumericField: (v: string | null) => void;
    isReadOnly: boolean;
    /** First printable key / Backspace / Delete on a grid landing target (Excel type-to-replace). */
    onPasteCellReplaceKey?: (row: number, col: number, key: string) => void;
}): void {
    const { containerRef, lineItemCount, setEditingNumericField, isReadOnly, onPasteCellReplaceKey } = options;

    const focusLineGridCell = useCallback(
        (row: number, col: number) => {
            const host = containerRef.current;
            if (!host) return;
            const td = host.querySelector(`td[data-line-paste-cell][data-row="${row}"][data-col="${col}"]`);
            if (!td) return;

            const blurActiveInsideHost = () => {
                const active = document.activeElement as HTMLElement | null;
                if (active && host.contains(active)) active.blur();
            };

            if (col >= 3) {
                blurActiveInsideHost();
                setEditingNumericField(null);
                window.setTimeout(() => {
                    (td.querySelector('[data-line-item-price-landing]') as HTMLElement | null)?.focus({
                        preventScroll: true,
                    });
                }, 0);
                return;
            }

            // Land on focus shell (not the Chakra input) so the cell looks selected until the user types or clicks in.
            blurActiveInsideHost();
            setEditingNumericField(null);
            window.setTimeout(() => {
                (td.querySelector('[data-line-item-grid-landing]') as HTMLElement | null)?.focus({ preventScroll: true });
            }, 0);
        },
        [containerRef, setEditingNumericField]
    );

    useLayoutEffect(() => {
        if (isReadOnly || lineItemCount <= 0) return;

        const onKeyDown = (e: KeyboardEvent) => {
            const host = containerRef.current;
            if (!host) return;

            if (onPasteCellReplaceKey && !isReadOnly) {
                const ae = document.activeElement;
                if (ae instanceof HTMLElement && host.contains(ae)) {
                    const landing = ae.closest('[data-line-item-grid-landing], [data-line-item-price-landing]');
                    if (landing && ae === landing) {
                        const tdL = landing.closest('td[data-line-paste-cell]');
                        if (tdL instanceof HTMLTableCellElement && host.contains(tdL)) {
                            const rowL = parseInt(tdL.getAttribute('data-row') || '', 10);
                            const colL = parseInt(tdL.getAttribute('data-col') || '', 10);
                            if (Number.isFinite(rowL) && Number.isFinite(colL)) {
                                const printable =
                                    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !e.isComposing;
                                const isClear = e.key === 'Backspace' || e.key === 'Delete';
                                if (printable || isClear) {
                                    e.preventDefault();
                                    e.stopImmediatePropagation();
                                    onPasteCellReplaceKey(rowL, colL, e.key);
                                    return;
                                }
                            }
                        }
                    }
                }
            }

            const isTab = e.key === 'Tab';
            const isEnter = isGridEnterKey(e);
            const arrowDir = gridArrowDirection(e);
            const isArrow = arrowDir != null;
            if (!isTab && !isEnter && !isArrow) return;

            const td = findPasteCellTd(e, host);
            if (!td) return;

            const row = parseInt(td.getAttribute('data-row') || '', 10);
            const col = parseInt(td.getAttribute('data-col') || '', 10);
            if (!Number.isFinite(row) || !Number.isFinite(col)) return;

            const lastRow = Math.max(lineItemCount - 1, lastPasteRowIndexFromDom(host));

            if (isTab) {
                const backward = e.shiftKey;
                const atStart = col === 0 && row === 0;
                const atEnd = col === NUM_VALUE_COLS - 1 && row === lastRow;
                if (backward && atStart) return;
                if (!backward && atEnd) return;
                let nr = row;
                let nc = col;
                if (backward) {
                    if (col > 0) nc = col - 1;
                    else {
                        nr = row - 1;
                        nc = NUM_VALUE_COLS - 1;
                    }
                } else if (col < NUM_VALUE_COLS - 1) {
                    nc = col + 1;
                } else {
                    nr = row + 1;
                    nc = 0;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                markEditableInputCommitOnBlur(td);
                focusLineGridCell(nr, nc);
                return;
            }

            if (isEnter && !e.shiftKey && !e.isComposing) {
                if (row >= lastRow) {
                    const ae = document.activeElement as HTMLElement | null;
                    if (ae && host.contains(ae)) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                        markEditableInputCommitOnBlur(td);
                        ae.blur();
                    }
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                markEditableInputCommitOnBlur(td);
                const ae = document.activeElement as HTMLElement | null;
                if (ae && host.contains(ae)) ae.blur();
                window.setTimeout(() => focusLineGridCell(row + 1, col), 0);
                return;
            }

            if (isArrow && arrowDir) {
                if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
                if (e.isComposing) return;
                let nr = row;
                let nc = col;
                if (arrowDir === 'ArrowLeft') {
                    if (caretBlocksHorizontalCellNav(td, e, 'left')) return;
                    if (col > 0) nc = col - 1;
                    else if (row > 0) {
                        nr = row - 1;
                        nc = NUM_VALUE_COLS - 1;
                    } else return;
                } else if (arrowDir === 'ArrowRight') {
                    if (caretBlocksHorizontalCellNav(td, e, 'right')) return;
                    if (col < NUM_VALUE_COLS - 1) nc = col + 1;
                    else if (row < lastRow) {
                        nr = row + 1;
                        nc = 0;
                    } else return;
                } else if (arrowDir === 'ArrowUp') {
                    if (row <= 0) return;
                    nr = row - 1;
                } else if (arrowDir === 'ArrowDown') {
                    if (row >= lastRow) {
                        const ae = document.activeElement as HTMLElement | null;
                        if (ae && host.contains(ae)) {
                            e.preventDefault();
                            e.stopImmediatePropagation();
                            ae.blur();
                        }
                        return;
                    }
                    nr = row + 1;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                focusLineGridCell(nr, nc);
            }
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [focusLineGridCell, isReadOnly, lineItemCount, containerRef, onPasteCellReplaceKey]);
}
